/**
 * ACX audio submission validation helpers.
 * Specs: https://help.acx.com/s/article/what-are-the-acx-audio-submission-requirements
 */

export type AcxCheckId =
  | "rms"
  | "peak"
  | "noiseFloor"
  | "sampleRate"
  | "duration"
  | "headRoomTone"
  | "tailRoomTone";

export type AcxCheckResult = {
  id: AcxCheckId;
  label: string;
  ok: boolean;
  detail: string;
};

export type AcxAudioMetrics = {
  rmsDb: number;
  peakDb: number;
  noiseFloorDb: number;
  sampleRateHz: number;
  durationSec: number;
  /** Seconds of near-silence at start. */
  headSilenceSec: number;
  /** Seconds of near-silence at end. */
  tailSilenceSec: number;
};

export const ACX_RMS_MIN = -23;
export const ACX_RMS_MAX = -18;
export const ACX_PEAK_MAX = -3;
export const ACX_NOISE_FLOOR_MAX = -60;
export const ACX_SAMPLE_RATE = 44100;
export const ACX_MAX_DURATION_SEC = 120 * 60;
export const ACX_HEAD_SILENCE_MIN = 0.5;
export const ACX_HEAD_SILENCE_MAX = 1;
export const ACX_TAIL_SILENCE_MIN = 1;
export const ACX_TAIL_SILENCE_MAX = 5;

export function validateAcxMetrics(metrics: AcxAudioMetrics): AcxCheckResult[] {
  return [
    {
      id: "rms",
      label: "RMS loudness",
      ok: metrics.rmsDb >= ACX_RMS_MIN && metrics.rmsDb <= ACX_RMS_MAX,
      detail: `${metrics.rmsDb.toFixed(1)} dB (need ${ACX_RMS_MIN}…${ACX_RMS_MAX})`,
    },
    {
      id: "peak",
      label: "Peak level",
      ok: metrics.peakDb <= ACX_PEAK_MAX,
      detail: `${metrics.peakDb.toFixed(1)} dB (max ${ACX_PEAK_MAX})`,
    },
    {
      id: "noiseFloor",
      label: "Noise floor",
      ok: metrics.noiseFloorDb <= ACX_NOISE_FLOOR_MAX,
      detail: `${metrics.noiseFloorDb.toFixed(1)} dB (max ${ACX_NOISE_FLOOR_MAX})`,
    },
    {
      id: "sampleRate",
      label: "Sample rate",
      ok: metrics.sampleRateHz === ACX_SAMPLE_RATE,
      detail: `${metrics.sampleRateHz} Hz (need ${ACX_SAMPLE_RATE})`,
    },
    {
      id: "duration",
      label: "Duration",
      ok: metrics.durationSec > 0 && metrics.durationSec <= ACX_MAX_DURATION_SEC,
      detail: `${(metrics.durationSec / 60).toFixed(1)} min (max 120)`,
    },
    {
      id: "headRoomTone",
      label: "Opening room tone",
      ok:
        metrics.headSilenceSec >= ACX_HEAD_SILENCE_MIN &&
        metrics.headSilenceSec <= ACX_HEAD_SILENCE_MAX + 0.25,
      detail: `${metrics.headSilenceSec.toFixed(2)}s (need ${ACX_HEAD_SILENCE_MIN}–${ACX_HEAD_SILENCE_MAX}s)`,
    },
    {
      id: "tailRoomTone",
      label: "Closing room tone",
      ok:
        metrics.tailSilenceSec >= ACX_TAIL_SILENCE_MIN &&
        metrics.tailSilenceSec <= ACX_TAIL_SILENCE_MAX,
      detail: `${metrics.tailSilenceSec.toFixed(2)}s (need ${ACX_TAIL_SILENCE_MIN}–${ACX_TAIL_SILENCE_MAX}s)`,
    },
  ];
}

export function acxAllPassed(checks: AcxCheckResult[]): boolean {
  return checks.every((c) => c.ok);
}

/** Linear gain to move current RMS toward target (clamped). */
export function gainForTargetRms(currentRmsDb: number, targetRmsDb: number): number {
  const delta = targetRmsDb - currentRmsDb;
  const linear = Math.pow(10, delta / 20);
  return Math.min(8, Math.max(0.125, linear));
}

export function dbFromLinear(linear: number): number {
  if (linear <= 0) return -100;
  return 20 * Math.log10(linear);
}

export function linearFromDb(db: number): number {
  return Math.pow(10, db / 20);
}
